import useApiContext from "@sb1finans/components/dist/ApiProvider"
import React, { createContext, useEffect, useState, useContext } from "react"
import { useCurrentAgreementId } from "../utils/route-hooks"
import { Account, FinancedObject, IncomingPayment, Invoice, InvoicePaymentStatusEnum, Transaction } from "@sb1finans/new-api/dist"
import { useParams } from "react-router-dom"
import { accountsApi, invoiceApi } from "../api"
import { getNextUnpaidInvoice } from "../account/invoiceSelection"


const emptyState = {
  accounts: [],
  objects: new Map(),
  invoices: new Map(),
  transactions: new Map(),
  paymentPlans: new Map(),
  loading: true,
  error: null
}

// Context creation
const AccountDataContext = createContext<AppDataState>(emptyState);

// Provider component

interface AppDataState {
    accounts: Account[]
    // Map of objects to account
    objects: Map<string, FinancedObject[]>
    // Map of invoices to account
    invoices: Map<string, Invoice[]>
    // Map of transactions to account
    transactions: Map<string, Transaction[]>
    // Map of payment plans to account
    paymentPlans: Map<string, IncomingPayment[]>
    loading: boolean
    error: Error | null
}

export const AppDataStateProvider = ({ children }) => {

    const agreementId = useCurrentAgreementId()

    const [state, setState] = useState<AppDataState>(emptyState)

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        console.log("Fetching data in app data state provider")
        const fetchData = async () => {
            try {
                console.log("Starting to fetch data in app data state provider")
                const accountsResponse = await accountsApi.listAccounts(agreementId)
                console.log("Got accounts", accountsResponse.data)
                setState(prevState => ({ ...prevState, accounts: accountsResponse.data }));
            
                // Fetch data for each account
                console.log("Fetching data for each account")
                await Promise.all(accountsResponse.data.map(async account => {
                  try {
                    console.log("Gt payment plans etc..")
                    const [paymentPlan, transactionList, invoices, objects] = await Promise.all([
                        accountsApi.getAccountPaymentPlan(agreementId, account.id).catch(err => ({ error: 'Failed to fetch payment plan', accountId: account.id })),
                        accountsApi.listAccountTransactions(agreementId, account.id).catch(err => ({ error: 'Failed to fetch transactions', accountId: account.id })),
                        invoiceApi.listAccountInvoices(agreementId, account.id).catch(err => ({ error: 'Failed to fetch invoices', accountId: account.id })),
                        accountsApi.listAccountObjects(agreementId, account.id).catch(err => ({ error: 'Failed to fetch account objects', accountId: account.id }))
                    ]);

                    console.log("Setting state in app data state provider")

                    setState(prevState => ({
                        ...prevState,
                        accounts: accountsResponse.data,
                        paymentPlans: prevState.paymentPlans.set(account.id, (paymentPlan as any).error ? [] : (paymentPlan as any).data),
                        transactions: prevState.transactions.set(account.id, (transactionList as any).error ? [] : (transactionList as any).data),
                        invoices: prevState.invoices.set(account.id, (invoices as any).error ? [] : (invoices as any).data),
                        objects: prevState.objects.set(account.id, (objects as any).error ? [] : (objects as any).data),
                    }))
                    // Update state with fetched data for each account
                    // Handle individual data parts separately if needed, especially for error handling
                    console.log(paymentPlan, transactionList, /*invoices*/); // Placeholder for state update logic
                  } catch (err) {
                    // Handle error for non-invoice failures, if any
                    console.error('Error fetching data for account', account.id, err);
                  }
                }));
            } catch (err) {
              setError(Error('Failed to fetch accounts'));
            } finally {
                console.log("Finished fetching data in app data state provider")
              setState(prevState => ({ ...prevState, loading: false }));
            }
        };

      fetchData();
    // Your fetching logic here (same as the previous useEffect)
  }, []);

  return (
    <AccountDataContext.Provider value={state}>
      {children}
    </AccountDataContext.Provider>
  );
};


export const useAccounts = () => {
  const state = useContext(AccountDataContext);
  console.log(state)
  return state.accounts;
}

export const useAccount = () => {
  const { accountId } = useParams<RouterParams>()
  const state = useContext(AccountDataContext);
  return state.accounts.find(account => account.id === accountId);
}

export const useObjects = () => {
  const state = useContext(AccountDataContext);
  return state.objects;
}

export const usePaymentPlan = (accountId: string) => {
  const state = useContext(AccountDataContext);
  return state.paymentPlans.get(accountId);
}

export const useTransactions = (accountId: string) => {
  const state = useContext(AccountDataContext);
  return state.transactions.get(accountId);
}

export const useInvoices = (accountId: string) => {
  const state = useContext(AccountDataContext);
  return state.invoices.get(accountId)
  ?.sort((a, b) => new Date(b.invoiceDate).getTime() - new Date(a.invoiceDate).getTime());
}

export const useUpcomingInvoices = () => {
  const state = useContext(AccountDataContext);
  // Get next invoice for each account
  const retMap = new Map<string, Invoice>();
  state.invoices.forEach((invoices, key) => {
    const nextInvoice = getNextUnpaidInvoice(invoices)
    if (nextInvoice) retMap.set(key, nextInvoice);
  })
  return retMap
}
